/**
 * Security review HIGH-010: helpers to validate postMessage origins and to send
 * to the embedding parent with an explicit target origin instead of '*'.
 *
 * The trusted parent origin is derived at runtime from
 * `window.location.ancestorOrigins` (Chromium/WebKit), falling back to the
 * `document.referrer` origin (Firefox), and finally to same-origin.
 *
 * Failure mode is safe: if the derived origin is wrong, a message is silently
 * NOT delivered (the feature breaks) — it never leaks data to an untrusted
 * origin, and incoming messages from unknown origins are dropped.
 */

const getAncestorOrigins = (): string[] => {
  try {
    const ao = (window.location as any).ancestorOrigins as
      | { length: number; [i: number]: string }
      | undefined;
    if (ao && ao.length) {
      return Array.from({ length: ao.length }, (_, i) => ao[i]);
    }
    if (document.referrer) {
      return [new URL(document.referrer).origin];
    }
  } catch {
    // ignore
  }
  return [];
};

/** Origin of the embedding parent window (or same-origin if not embedded cross-origin). */
export const getParentOrigin = (): string =>
  getAncestorOrigins()[0] || window.location.origin;

/** Post a message to the parent window using an explicit origin (never '*'). */
export const postToParent = (message: any): void => {
  window.parent.postMessage(message, getParentOrigin());
};

/** True when `origin` is a trusted embedding-parent origin. */
export const isTrustedParentOrigin = (origin: string): boolean => {
  if (!origin) {
    return false;
  }
  const trusted = getAncestorOrigins();
  return (trusted.length ? trusted : [window.location.origin]).includes(origin);
};

/** Wrap a message handler so it only runs for messages from a trusted parent origin. */
export const onParentMessage = (handler: (event: MessageEvent) => void) => (
  event: MessageEvent
): void => {
  if (isTrustedParentOrigin(event.origin)) {
    handler(event);
  }
};

/**
 * True when `origin` matches the origin of a known iframe `src` URL. Use for
 * receivers that listen to a child iframe they render (e.g. DocuSign / identity
 * verification), where the trusted origin is the iframe URL's origin.
 */
export const originMatchesUrl = (
  url: string | null | undefined,
  origin: string
): boolean => {
  if (!url || !origin) {
    return false;
  }
  try {
    return new URL(url).origin === origin;
  } catch {
    return false;
  }
};
